import Typography from '@material-ui/core/Typography';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Elements, injectStripe, StripeProvider } from 'react-stripe-elements';
import { AppConfig } from '../../../../config/app';
import { DataApi } from '../../../../providers/api-provider';
import GlobalStore from '../../../../providers/global-store-provider';
import { calculateTotalFees, formatCurrency, handleInputState, handleResponseErrors, postMessageToParent, showError, showSuccess, track } from '../../libs/helpers';
import CampaignSeedAmount from './CampaignSeedAmount';
import CampaignSeedPersonal from './CampaignSeedPersonal';

class CampaignSeedForm extends React.Component {
    constructor(props) {
        super(props);
        this.onChange = this.onChange.bind(this);
    }
    state = {
        stepExpanded: 'chooseAmount',
        hearFromFunds: [],
        cardBrand: 'visa',
        cardComplete: false,
        loading: false,
        triggerExpressPay: false,
        form: {
            fullAmount: false,
            buttonAmount: !this.props.match.params.perkId ? 500 : '',
            anonymous: false,
            donorAlias: this.props.match.params.name || '',
            showDonorEmail: false,
            donorCoverFees: true,
            perkId: this.props.match.params.perkId || '',
            amount: '',
            processedAmount: '',
            paymentMethod: '',
            donorEmail: this.props.match.params.email || '',
            hearFromFundId: 'other',
            donorLike: 1,
            donorComment: '',
            dedicateMyDonation: false,
            inHonorOf: true,
            honoreeName: '',
            recipientEmail: '',
            recipientName: '',
            recipientMessage: '',
        },
    }
    /*
    -----------------------------------------------------------------------
    API Functions
    -----------------------------------------------------------------------
    */
    /* API Call
    * Can perform 2 types of seeding(donation):
    * 1- Normal Seed (donation) where user fill-out the checkout and press on donate.
    * 2- Instant Seed (donation) which happens when the user decides to seed a similar campaign after normal seed.
    */
    performSeedAction = async (paymentMethod, name, email, paymentIntent) => {
        let form = { ...this.state.form };
        const instantDonate = this.props.instantDonate
        //Required after authorizing payments by donor (3d Secure)
        if (paymentIntent)
            form.paymentIntent = paymentIntent;
        //
        if (name && email) {
            form.donorAlias = name;
            form.donorEmail = email;
        } else if (!this.validateInformation())
            return;
        try {
            postMessageToParent('donation_started', {
                title: this.props.trackPageCode,
            });
            track(this.props.trackPageCode, 'Donation Button Clicked');
            this.setState({ loading: true });
            if (!paymentMethod) {
                const paymentMethodRes = await this.props.stripe.createPaymentMethod('card');
                if (paymentMethodRes.error) {
                    showError(paymentMethodRes.error.message);
                    this.setState({ loading: false });
                    track(this.props.trackPageCode, 'Donation Failed', paymentMethodRes.error.message);
                    postMessageToParent('donation_error', {
                        title: this.props.trackPageCode,
                        message: paymentMethodRes.error.message,
                    });
                    return false;
                }
                paymentMethod = paymentMethodRes.paymentMethod;
            }
            form.paymentMethod = paymentMethod.id;
            let url = 'donor/campaigns/' + (instantDonate || this.props.match.params.slug) + '/seed-anonymous';
            //If it was a featured campaign in a Fund
            //if (this.props.match.params.fund)
            //  url = 'donor/fundraises/' + this.props.match.params.fund + '/seed-anonymous-campaigns/' + this.props.match.params.slug;
            if (form.buttonAmount)
                form.amount = form.buttonAmount;
            //if instant donation ignore recapatcha
            if (instantDonate) {
                form.ignoreRecaptchaSecret = 'edSeed-' + instantDonate;
                form.donorComment = '';
                form.donorLike = 0;
            }
            if (form.hearFromFundId === 'other')
                form.hearFromFundId = '';
            if (this.props.recaptchaEnabled)
                form.recaptcha = this.props.recaptchaRef.current ? await this.props.recaptchaRef.current.executeAsync() : null;

            //Do the seed
            const donationRes = await DataApi.update(url, form, this.props.match.params.token ? {
                'Authorization': "Bearer " + this.props.match.params.token
            } : null);
            //Post donation
            this.onDonationSucceed(donationRes, instantDonate);
            return true;
        }
        catch (error) {
            if (this.props.recaptchaRef && this.props.recaptchaRef.current) {
                this.props.recaptchaRef.current.reset();
            }
            const errorRes = error.response.data;
            //Check for required actions
            if (errorRes.requires_action) {
                const actionRes = await this.props.stripe.confirmCardPayment(errorRes.payment_intent.client_secret, {
                    payment_method: form.paymentMethod,
                });
                if (actionRes.paymentIntent && actionRes.paymentIntent.status === "succeeded") {
                    this.performSeedAction(paymentMethod, form.donorAlias, form.donorEmail, actionRes.paymentIntent.id)
                    return;
                }
            }
            let newState = this.state;
            //Show SnackBar
            const message = showError(error);
            newState.errors = handleResponseErrors(error, this.state.errors);
            newState.loading = false;
            this.setState(newState);
            postMessageToParent('donation_error', {
                title: this.props.trackPageCode,
                message: message,
            });
            track(this.props.trackPageCode, 'Donation Failed', message);
            this.props.onDonationFail();

        }
        return false;
    }

    async componentDidMount() {
        let form = this.state.form;
        if (this.props.match.params.amount) {
            form.amount = this.props.match.params.amount
        }
        if (GlobalStore.state.auth.loggedIn && !form.donorEmail) {
            form.donorEmail = GlobalStore.state.auth.user.email;
            form.donorAlias = GlobalStore.state.auth.user.name;
        }
        this.setState({ form }, () => this.onChangeAmount(this.state.form));
        track(this.props.trackPageCode, 'Checkout Loaded');
    }
    componentDidUpdate(prevProps) {
        if (this.props.instantDonate && !prevProps.instantDonate) {
            if (!this.state.cardComplete) //Google or Apple pay
                this.setState({ triggerExpressPay: true }, () => this.setState({ triggerExpressPay: false }));
            else //Credit card pay
                this.performSeedAction();
        }
    }
    /*
    -----------------------------------------------------------------------
    RENDER Functions
    -----------------------------------------------------------------------
    */
    onPanelExpanded = (step) => {
        return () => {
            let steps = { donorInformation: 'Donor Information', chooseAmount: 'Choose Amount', checkout: 'Card Information', moreInformation: 'Give More' }
            this.setState({ 'stepExpanded': this.state.stepExpanded === step ? '' : step });
            track(this.props.trackPageCode, steps[step] + ' Opened');
        }
    }
    onFieldBlur = (field) => {
        return async (e) => {
            track(
                this.props.trackPageCode,
                field + (e.target.type !== "checkbox" ? ' Filled' : ''),
                e.target.type === "checkbox"
                    ? (e.target.checked ? 'Checked' : 'Not Checked')
                    : e.target.value);
        }
    }
    /* on Donation Success */
    onDonationSucceed = (donationRes, instantDonate) => {
        //Reset Captcha
        if (this.props.recaptchaRef && this.props.recaptchaRef.current) {
            this.props.recaptchaRef.current.reset();
        }
        //Show SnackBar
        showSuccess('Donation Completed, Thank you!', 'success');
        track(this.props.trackPageCode, 'Donation Succeeded');
        /*  if (window.history.length > 0)
           window.history.back()
         else */
        //Post message to edseed.me
        postMessageToParent('campaign_seed', {
            transaction_id: donationRes.data.online_donation.transaction_id,
            amount: donationRes.data.online_donation.amount,
            fees: donationRes.data.online_donation.fees,
            title: this.props.campaign.student.name + "'s Campaign",
            total_donations: donationRes.data.campaign_total_donations,
            campaign_id: this.props.campaign.id,
            total_needs: this.props.campaign.total_needs,
            total_donations_formatted: formatCurrency(donationRes.data.campaign_total_donations),
            fund: this.props.match.params.fund || null,
        });
        //Prepare instant donate form:
        let amount = donationRes.data.online_donation.amount;
        if (this.state.form.donorCoverFees)
            amount -= donationRes.data.online_donation.fees + donationRes.data.online_donation.house_fees;
        this.setState({ loading: false, form: { ...this.state.form, amount: amount, perkId: '' } });
        this.props.onDonationComplete(this.state.form.processedAmount, donationRes.data.online_donation.id);
    }
    /* Validate Form */
    validateInformation = () => {
        if (!this.state.form.processedAmount) {
            showError('Fill out the amount please');
            this.onPanelExpanded('chooseAmount')();
            return false;
        }
        if (!this.state.form.donorEmail) {
            showError('Email is mandatory');
            return false;
        }
        if (!this.state.form.donorAlias) {
            showError('Name is mandatory');
            return false;
        }
        /* if (this.state.form.dedicateMyDonation) {
            if (!this.state.form.honoreeName) {
                showError('Honoree Name is mandatory');
                this.onPanelExpanded('chooseAmount')();
                return false;
            }
            if (!this.state.form.recipientEmail) {
                showError('Recipient Email is mandatory');
                this.onPanelExpanded('chooseAmount')();
                return false;
            }
        } */
        return true;
    }
    cancelExpressPay = () => {
        track(this.props.trackPageCode, 'Express Donation Cancelled');
        this.props.onDonationFail();
    }
    /* Hanle State Management of form */
    onChange = handleInputState
    onChangeRoot = (newState, updateAmount) => {
        this.setState(newState);
        if (updateAmount && this.state.processedAmount)
            this.onChangeAmount(this.state.form);
    }
    onChangeForm = (key, value, updateAmount) => {
        //Multi Key Update
        if (typeof (key) === 'object') {
            this.setState({ form: { ...this.state.form, ...key } },
                () => value && this.onChangeAmount(this.state.form)
            );
        }
        else
            //Single Key update
            this.setState({ form: { ...this.state.form, [key]: value } },
                () => updateAmount && this.onChangeAmount(this.state.form)
            );
    }

    onChangeAmount = (amountDetails) => {
        let processedAmount = parseFloat(amountDetails.amount) || '';
        if (amountDetails.buttonAmount)
            processedAmount = amountDetails.buttonAmount;
        if (amountDetails.fullAmount)
            processedAmount = this.props.campaign.needed_donations;
        if (amountDetails.perkId)
            processedAmount = this.props.campaign.perks.find((i) => i.id === parseInt(amountDetails.perkId)).amount;
        if (this.state.form.donorCoverFees) {
            processedAmount = calculateTotalFees(processedAmount, this.state.cardBrand === 'amex');
        }
        this.setState({ form: { ...this.state.form, ...amountDetails, processedAmount: processedAmount } });
    }

    /* Render Page */
    render() {
        const { stepExpanded, form, loading, triggerExpressPay } = this.state;
        let { campaign, trackPageCode, classes } = this.props;
        return (
            <div className="fullwidth">
                {/*Intro One */}
                <div>
                    <Typography className={classes.headerText}>
                        DONATE
                    </Typography>
                    <div className={classes.headerBorder}>
                        <div className={classes.headerMainBorder}></div>
                    </div>

                    {/* <div className={classes.campaignInformationWrapperName}>
                        <Typography variant="body1" className={classes.campaignName}>
                            {campaign.name} campaign
                        </Typography>
                        <Typography variant="body1" className={classes.campaignDonations}>
                            Collected ${formatCurrency(campaign.total_donations)} of ${formatCurrency(campaign.total_needs)}
                        </Typography>
                    </div> */}
                </div>
                {/*Step One */}
                <div className={stepExpanded === 'chooseAmount' ? classes['stepActive'] : classes['stepInactive']} >
                    <CampaignSeedAmount
                        onFieldBlur={this.onFieldBlur}
                        form={form}
                        onChange={this.onChangeForm}
                        onPanelExpanded={this.onPanelExpanded}
                        trackPageCode={trackPageCode}
                        classes={classes}
                        campaign={campaign}
                    />
                </div>
                {/*Step Two */}
                <div className={stepExpanded === 'donorInformation' ? classes['stepActive'] : classes['stepInactive']}>
                    <CampaignSeedPersonal
                        onFieldBlur={this.onFieldBlur}
                        form={form}
                        onChange={this.onChangeForm}
                        onChangeRoot={this.onChangeRoot}
                        trackPageCode={trackPageCode}
                        onPanelExpanded={this.onPanelExpanded}
                        classes={classes}
                        performSeedAction={this.performSeedAction}
                        loading={loading}
                        stripe={this.props.stripe}
                        campaign={campaign}
                        triggerExpressPay={triggerExpressPay}
                        cancelExpressPay={this.cancelExpressPay}
                    />
                </div>
            </div>
        );
    }
}

const CampaignSeedFormWrapped = injectStripe(withRouter(CampaignSeedForm));
const stripeKey = ['localhost:3000', 'localhost:81', 'staging-edp.edseed.me'].indexOf(window.location.host) !== -1
    ? AppConfig.stripeTest : AppConfig.stripe;
export default function (props) {
    return (<StripeProvider apiKey={stripeKey}>
        <Elements>
            <CampaignSeedFormWrapped {...props} />
        </Elements>
    </StripeProvider>);

}